import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Warning, WarningCircle, Info, CheckCircle, ArrowRight, Gear, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const LEVEL_STYLES = {
  critical: { bg: "#0A0A0A", fg: "#FFFFFF", accent: "#DDA7A5", icon: WarningCircle },
  warning:  { bg: "#FAE6CB", fg: "#7A4B12", accent: "#E2B77B", icon: Warning },
  info:     { bg: "#F5F0EB", fg: "#0A0A0A", accent: "#0A0A0A", icon: Info },
  success:  { bg: "#DCEBDD", fg: "#2D5A33", accent: "#7B9D7E", icon: CheckCircle },
};

export default function AlertCenter() {
  const [data, setData] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cl_alerts_dismissed") || "[]"); } catch { return []; }
  });
  const [thresholds, setThresholds] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/alerts");
      setData(data);
      setThresholds(data.thresholds);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("cl_alerts_dismissed", JSON.stringify(next));
  };

  const saveThresholds = async () => {
    try {
      await api.post("/alerts/thresholds", {
        fund_coverage_pct: Number(thresholds.fund_coverage_pct),
        default_rate_max: Number(thresholds.default_rate_max),
        late_installments_max: Number(thresholds.late_installments_max),
        risk_clients_max: Number(thresholds.risk_clients_max),
      });
      toast.success("Limites atualizados");
      setShowConfig(false);
      localStorage.removeItem("cl_alerts_dismissed");
      setDismissed([]);
      load();
    } catch { toast.error("Erro"); }
  };

  if (!data) return null;
  const visible = data.alerts.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-3 mb-8" data-testid="alert-center">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">
          Alertas automaticos · {data.metrics.fund_coverage_pct !== null ? `cobertura ${data.metrics.fund_coverage_pct}%` : ""}
        </div>
        <button onClick={() => setShowConfig(!showConfig)} className="text-[10px] tracking-widest uppercase text-[#5C5A57] hover:text-black flex items-center gap-1" data-testid="alerts-config-btn">
          <Gear size={12} /> Limites
        </button>
      </div>

      {visible.map((a) => {
        const s = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
        const Icon = s.icon;
        return (
          <div
            key={a.id}
            className="border p-5 flex items-start gap-4"
            style={{ background: s.bg, color: s.fg, borderColor: s.accent }}
            data-testid={`alert-${a.id}`}
          >
            <Icon size={26} weight="duotone" style={{ color: s.accent, flexShrink: 0 }} />
            <div className="flex-1">
              <div className="font-display text-lg font-bold tracking-tight">{a.title}</div>
              <div className="text-sm opacity-90 mt-1">{a.detail}</div>
              {a.action && (
                <div className="text-xs mt-2 opacity-80">
                  <strong>Acao recomendada:</strong> {a.action}
                </div>
              )}
              {a.link && (
                <Link to={a.link} className="inline-flex items-center gap-1 text-xs tracking-widest uppercase font-medium mt-3 hover:underline" style={{ color: s.accent }} data-testid={`alert-link-${a.id}`}>
                  Resolver <ArrowRight size={12} />
                </Link>
              )}
            </div>
            <button onClick={() => dismiss(a.id)} className="opacity-60 hover:opacity-100" data-testid={`alert-dismiss-${a.id}`}>
              <X size={16} />
            </button>
          </div>
        );
      })}

      {showConfig && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={() => setShowConfig(false)}>
          <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Sinaleira financeira</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1 mb-6">Limites de alerta</h2>

            <div className="space-y-5">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Cobertura minima do Fundo (%)</label>
                <input type="number" step="0.5" min="0" max="100" value={thresholds.fund_coverage_pct || ""} onChange={(e) => setThresholds({ ...thresholds, fund_coverage_pct: e.target.value })} className="input-underline tabular" data-testid="th-fund-input" />
                <div className="text-[10px] text-[#5C5A57] mt-1">Saldo do fundo vs vendas do mes</div>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Inadimplencia maxima (%)</label>
                <input type="number" step="0.5" min="0" max="100" value={thresholds.default_rate_max || ""} onChange={(e) => setThresholds({ ...thresholds, default_rate_max: e.target.value })} className="input-underline tabular" data-testid="th-default-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Parcelas atrasadas (max)</label>
                <input type="number" step="1" min="0" value={thresholds.late_installments_max || ""} onChange={(e) => setThresholds({ ...thresholds, late_installments_max: e.target.value })} className="input-underline tabular" data-testid="th-late-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Clientes de risco (max)</label>
                <input type="number" step="1" min="0" value={thresholds.risk_clients_max || ""} onChange={(e) => setThresholds({ ...thresholds, risk_clients_max: e.target.value })} className="input-underline tabular" data-testid="th-risk-input" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setShowConfig(false)} className="text-xs tracking-widest uppercase text-[#5C5A57] hover:underline">Cancelar</button>
              <button onClick={saveThresholds} className="brand" data-testid="save-thresholds-btn">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
